import { useIntl } from '@capsicum/common';
import { contentWidth, contentWidthDesktop } from '@capsicum/common-web';
import { ExternalLinkIcon } from '@capsicum/common-web/icons';
import { type HeaderWarningPage, useShowHeaderWarning } from '@capsicum/features/regulations/shared';
import { useTrackUKRegulationEvents } from '@capsicum/socios-core';
import { forwardRef, type ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';

export type RiskWarningProps = {
	horizontalPadding?: boolean;
	isGatewayPage?: boolean;
	page: HeaderWarningPage;
	transparentBackground?: boolean;
	useContentMaxWidth?: boolean;
	verticalPadding?: boolean;
};

export const HeaderWarning = forwardRef(function HeaderWarning(
	{
		horizontalPadding,
		isGatewayPage,
		page,
		transparentBackground,
		useContentMaxWidth,
		verticalPadding,
		...props
	}: RiskWarningProps,
	ref: React.ForwardedRef<HTMLDivElement>
) {
	const { colors } = useTheme();
	const { $t } = useIntl();
	const { riskLink, shouldShowHeaderWarning } = useShowHeaderWarning(isGatewayPage);
	const { trackUKBannerClick } = useTrackUKRegulationEvents();

	if (!shouldShowHeaderWarning(page)) return null;

	return (
		<S.Wrapper
			$horizontalPadding={horizontalPadding}
			$transparentBackground={transparentBackground}
			$useContentMaxWidth={useContentMaxWidth}
			$verticalPadding={verticalPadding}
			{...props}
			ref={ref}
		>
			<S.Container $useContentMaxWidth={useContentMaxWidth}>
				<S.Description>
					{$t('jurisdictionRequirements.headerWarning.gb', {
						link: (chunks: Array<ReactNode>) => (
							<S.Link
								data-testid={`${page}-header-warning-link`}
								href={riskLink}
								target="_blank"
								onClick={trackUKBannerClick}
							>
								{chunks}
								<S.ExternalLink color={colors.icons.brand.enabled} height={16} width={16} />
							</S.Link>
						)
					})}
				</S.Description>
			</S.Container>
		</S.Wrapper>
	);
});

namespace S {
	export const Wrapper = styled.div<{
		$horizontalPadding?: boolean;
		$transparentBackground?: boolean;
		$useContentMaxWidth?: boolean;
		$verticalPadding?: boolean;
	}>`
		background-color: ${({ $transparentBackground, theme }) =>
			$transparentBackground ? 'transparent' : theme.colors.backgrounds.main.screen};
		padding: ${({ $horizontalPadding, $verticalPadding, theme }) =>
			`${$verticalPadding ? theme.spacings.xsmall : 0} ${$horizontalPadding ? theme.spacings.xsmall : 0}`};
		position: sticky;
		z-index: 99;

		${({ $useContentMaxWidth }) => $useContentMaxWidth && `display: flex; justify-content: center;`}
	`;

	export const Container = styled.div<{
		$useContentMaxWidth?: boolean;
	}>`
		background-color: ${({ theme }) => theme.colors.backgrounds.info.subtle};
		${({ theme }) => theme.border.radius.medium};
		border-color: ${({ theme }) => theme.colors.borders.brand.enabled};
		border-style: solid;
		border-width: ${({ theme }) => theme.border.width.small};
		padding: ${({ theme }) => theme.spacings.xsmall};

		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			${({ $useContentMaxWidth }) => $useContentMaxWidth && `max-width: ${contentWidth}px;`}
		}

		${({ theme }) => theme.breakpoints.queries.above.mediumDesktop} {
			${({ $useContentMaxWidth }) => $useContentMaxWidth && `max-width: ${contentWidthDesktop}px;`}
		}
	`;

	export const Description = styled.span`
		${({ theme }) => theme.fonts.text.text2};
		line-height: ${({ theme }) => theme.fonts.text.text5['line-height']};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Link = styled.a`
		${({ theme }) => theme.fonts.text.text2};
		line-height: ${({ theme }) => theme.fonts.text.text5['line-height']};
		text-decoration: underline;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const ExternalLink = styled(ExternalLinkIcon)`
		display: inline-block;
		margin: ${({ theme }) => theme.spacings.xxxsmall};
		vertical-align: middle;
	`;
}
